import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, ArrowRight, Package, Store, AlertTriangle, History, Check, X, Calendar, ScanBarcode, Trash2, ListPlus,
} from "lucide-react";
import BarcodeScannerDialog from "@/pages/BarcodeScannerDialog";
import { Minus, Plus } from "lucide-react";

// Get expiry status info
const getExpiryStatus = (daysLeft) => {
  if (daysLeft === null || daysLeft === undefined) return { color: "bg-gray-100 text-gray-500", label: "N/A", priority: 999 };
  if (daysLeft < 0) return { color: "bg-red-100 text-red-700", label: "Expired", priority: -1 };
  if (daysLeft <= 30) return { color: "bg-red-50 text-red-700 border-red-200", label: `${daysLeft}d`, priority: 1 };
  if (daysLeft <= 60) return { color: "bg-orange-50 text-orange-700 border-orange-200", label: `${daysLeft}d`, priority: 2 };
  if (daysLeft <= 90) return { color: "bg-yellow-50 text-yellow-700 border-yellow-200", label: `${daysLeft}d`, priority: 3 };
  return { color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `${daysLeft}d`, priority: 4 };
};

export default function StockTransfer({ shops, prefill, onPrefillConsumed }) {
  // Search mode: "search" | "scan"
  const [searchMode, setSearchMode] = useState("search");
  const [scanOpen, setScanOpen] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productStock, setProductStock] = useState(null);
  
  const [sourceShop, setSourceShop] = useState("");
  const [destShop, setDestShop] = useState("");
  const [transferring, setTransferring] = useState(false);
  
  // Track recommended qty from prefill
  const [prefillRecommendedQty, setPrefillRecommendedQty] = useState(null);
  
  // Stale recommendation tracking (Phase 2D)
  const [prefillWarning, setPrefillWarning] = useState(null); // { type: 'partial' | 'zero', recommendedQty, actualQty }
  
  // Remember last used shops for quick transfers - persist in localStorage
  const [lastUsedSource, setLastUsedSource] = useState(() => {
    try { return localStorage.getItem("transfer_last_source") || ""; }
    catch { return ""; } // localStorage unavailable (private browsing)
  });
  const [lastUsedDest, setLastUsedDest] = useState(() => {
    try { return localStorage.getItem("transfer_last_dest") || ""; }
    catch { return ""; } // localStorage unavailable (private browsing)
  });
  
  // Batch selection state
  const [selectedBatches, setSelectedBatches] = useState({});
  // Format: { batchId: { selected: true, quantity: 5 } }
  
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Multi-product transfer list
  // Each item: { id, product, productStock, sourceShop, destShop, batches: [{batch_id, quantity, mrp, expiry_date}] }
  const [transferList, setTransferList] = useState([]);

  // Search products
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/products", { params: { q: query, limit: 10 } });
      setSearchResults(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
    // No external deps — api/toast/formatApiError are module-level imports
    // and setSearchResults/setSearching are stable React setters.
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => searchProducts(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchProducts]);

  // Handle prefill from Rebalancer recommendation
  useEffect(() => {
    if (!prefill || !prefill.productId) return;
    
    const loadPrefill = async () => {
      try {
        // First try to fetch stock for the product to verify it exists
        const stockResp = await api.get(`/stock/product-stock-summary/${prefill.productId}`);
        
        if (!stockResp.data || !stockResp.data.product) {
          toast.error(`Product not found: ${prefill.productName}`);
          if (onPrefillConsumed) onPrefillConsumed();
          return;
        }
        
        const productData = stockResp.data.product;
        
        // Build a product object from stock summary data
        const product = {
          id: prefill.productId,
          product: productData.name || prefill.productName,
          brand_name: productData.brand_name || "",
          group_name: productData.group_name || "",
          auto_barcode: productData.auto_barcode || "",
          manual_barcode: productData.manual_barcode || "",
          mrp: productData.mrp || null,
        };
        
        // Clear existing state
        setTransferList([]);
        setSelectedBatches({});
        
        // Set shops first
        setSourceShop(prefill.sourceShopId);
        setDestShop(prefill.destShopId);
        
        // Save recommended qty for auto-selection
        setPrefillRecommendedQty(prefill.recommendedQty);
        
        // Select the product
        setSelectedProduct(product);
        setSearchResults([]);
        setSearchQuery("");
        
        // Set the stock data we already fetched
        setProductStock(stockResp.data);
        
        toast.info(
          `Transfer prefilled: ${prefill.recommendedQty} units of ${prefill.productName} from ${prefill.sourceShopName} to ${prefill.destShopName}`,
          { duration: 5000 }
        );
        
        // Mark prefill as consumed
        if (onPrefillConsumed) onPrefillConsumed();
      } catch (e) {
        toast.error(`Failed to load product: ${formatApiError(e)}`);
        if (onPrefillConsumed) onPrefillConsumed();
      }
    };
    
    loadPrefill();
  }, [prefill, onPrefillConsumed]);

  // Fetch product stock summary
  const fetchProductStock = async (productId) => {
    try {
      const { data } = await api.get(`/stock/product-stock-summary/${productId}`);
      setProductStock(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Handle barcode scan result
  const handleBarcodeScan = async (barcode) => {
    if (!barcode) return;
    setScanOpen(false);
    
    try {
      // Search for product by barcode
      const { data } = await api.get("/products", { params: { q: barcode, limit: 5 } });
      const items = data.items || [];
      
      if (items.length === 0) {
        toast.error(`No product found for barcode: ${barcode}`);
        return;
      }
      
      // If exact barcode match, select immediately
      const exactMatch = items.find(
        (p) => p.manual_barcode === barcode || p.auto_barcode === barcode
      );
      
      if (exactMatch) {
        selectProduct(exactMatch);
        toast.success(`Found: ${exactMatch.product}`);
      } else {
        // Show results for user to choose
        setSearchResults(items);
        setSearchQuery(barcode);
        toast.info("Multiple products found. Please select one.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Select product - use remembered shops if available and valid
  // If transfer list has items, lock to those shops
  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSearchResults([]);
    setSearchQuery("");
    
    // If transfer list already has items, use those shops (locked)
    if (transferList.length > 0) {
      const firstItem = transferList[0];
      setSourceShop(firstItem.sourceShop);
      setDestShop(firstItem.destShop);
    } else {
      // Use last used shops if available AND they exist in the shops list
      const shopIds = shops.map(s => s.id);
      const validSource = lastUsedSource && shopIds.includes(lastUsedSource) ? lastUsedSource : "";
      const validDest = lastUsedDest && shopIds.includes(lastUsedDest) ? lastUsedDest : "";
      
      setSourceShop(validSource);
      setDestShop(validDest);
    }
    setSelectedBatches({});
    fetchProductStock(product.id);
  };

  // Clear selection - but remember the shops
  const clearSelection = () => {
    setSelectedProduct(null);
    setProductStock(null);
    // Keep sourceShop and destShop in lastUsed state for next product
    setSourceShop("");
    setDestShop("");
    setSelectedBatches({});
    setPrefillWarning(null); // Clear stale recommendation warning
  };

  // Get shop stock
  const getShopStock = (shopId) => {
    if (!productStock?.shops) return 0;
    const shop = productStock.shops.find(s => s.shop_id === shopId);
    return shop?.total_quantity || 0;
  };

  // Get batches for source shop, sorted by expiry (FEFO)
  const sourceBatches = useMemo(() => {
    if (!productStock?.shops || !sourceShop) return [];
    const shop = productStock.shops.find(s => s.shop_id === sourceShop);
    if (!shop?.batches) return [];
    
    // Sort by expiry date (earliest first - FEFO), then by MRP
    return [...shop.batches].sort((a, b) => {
      const aExp = a.days_until_expiry ?? 9999;
      const bExp = b.days_until_expiry ?? 9999;
      if (aExp !== bExp) return aExp - bExp;
      return (a.mrp || 0) - (b.mrp || 0);
    });
  }, [productStock, sourceShop]);

  // Reset batch selection when source shop changes
  useEffect(() => {
    setSelectedBatches({});
    // setSelectedBatches is a stable React setter — intentionally keyed only
    // on sourceShop to reset batch selection when changing source.
  }, [sourceShop]);

  // Phase 2D: Stale recommendation detection and auto-batch selection
  // This effect handles THREE scenarios:
  // A) Full stock available (recommended qty <= available) - normal flow
  // B) Partial stock available (0 < available < recommended) - warning
  // C) Zero stock available - error, cannot proceed
  useEffect(() => {
    // Only run when we have a prefill qty AND productStock is loaded for the prefilled source shop
    if (!prefillRecommendedQty || !productStock?.shops || !sourceShop) return;
    
    // Find stock data for the source shop
    const sourceShopStock = productStock.shops.find(s => s.shop_id === sourceShop);
    const batches = sourceShopStock?.batches || [];
    
    // Scenario C: Zero stock available at source
    if (batches.length === 0 || sourceShopStock?.total_quantity === 0) {
      setPrefillWarning({
        type: 'zero',
        recommendedQty: prefillRecommendedQty,
        actualQty: 0
      });
      toast.error(
        `This recommendation is no longer available. The source shop currently has no transferable stock.`,
        { duration: 8000 }
      );
      // Clear prefill qty immediately to prevent re-trigger on source shop change
      setPrefillRecommendedQty(null);
      return;
    }
    
    // Only auto-select if no batches are currently selected (prevent re-running)
    if (Object.keys(selectedBatches).length > 0) {
      // Already have selection - don't re-select, just clear prefill flag
      setPrefillRecommendedQty(null);
      return;
    }
    
    // Auto-select batches using FEFO (First Expired First Out) up to recommended qty
    // Sort batches by expiry (earliest first)
    const sortedBatches = [...batches].sort((a, b) => {
      const aExp = a.days_until_expiry ?? 9999;
      const bExp = b.days_until_expiry ?? 9999;
      if (aExp !== bExp) return aExp - bExp;
      return (a.mrp || 0) - (b.mrp || 0);
    });
    
    let remaining = prefillRecommendedQty;
    const newSelection = {};
    let totalSelected = 0;
    
    for (const batch of sortedBatches) {
      if (remaining <= 0) break;
      const qtyToSelect = Math.min(remaining, batch.quantity);
      if (qtyToSelect > 0) {
        newSelection[batch.id] = { selected: true, quantity: qtyToSelect };
        totalSelected += qtyToSelect;
        remaining -= qtyToSelect;
      }
    }
    
    // Scenario B: Partial quantity available
    if (totalSelected > 0 && totalSelected < prefillRecommendedQty) {
      setPrefillWarning({
        type: 'partial',
        recommendedQty: prefillRecommendedQty,
        actualQty: totalSelected
      });
      toast.warning(
        `Stock availability has changed. Only ${totalSelected} of the recommended ${prefillRecommendedQty} units are currently available.`,
        { duration: 8000 }
      );
    } else if (totalSelected >= prefillRecommendedQty) {
      // Scenario A: Full quantity available
      setPrefillWarning(null);
    }
    
    if (Object.keys(newSelection).length > 0) {
      setSelectedBatches(newSelection);
    }
    
    // Clear prefill qty so it doesn't re-trigger on subsequent source shop changes
    setPrefillRecommendedQty(null);
    
  }, [prefillRecommendedQty, productStock, sourceShop, selectedBatches]);

  // Toggle batch selection - default quantity is 1 (minimum for transfer)
  const toggleBatch = (batchId, maxQty) => {
    setSelectedBatches(prev => {
      const current = prev[batchId];
      if (current?.selected) {
        // Deselect - remove from selection
        const next = { ...prev };
        delete next[batchId];
        return next;
      } else {
        // Select - start with quantity 1 (or max if less than 1)
        const defaultQty = maxQty >= 1 ? 1 : maxQty;
        return { ...prev, [batchId]: { selected: true, quantity: defaultQty } };
      }
    });
  };

  // Update batch quantity - does NOT uncheck when value is empty/0
  const updateBatchQty = (batchId, qty, maxQty) => {
    // Allow empty string for user to clear and type new value
    // Only clamp when there's an actual number
    let val = qty;
    if (qty !== "" && qty !== null) {
      const numVal = parseInt(qty, 10);
      if (!isNaN(numVal)) {
        val = Math.max(0, Math.min(numVal, maxQty));
      }
    }
    
    setSelectedBatches(prev => {
      const current = prev[batchId];
      // Keep the checkbox selected regardless of the quantity value
      // User explicitly unchecks if they want to deselect
      return {
        ...prev,
        [batchId]: { selected: current?.selected ?? false, quantity: val }
      };
    });
  };

  // Increment batch quantity
  const incrementBatchQty = (batchId, maxQty) => {
    setSelectedBatches(prev => {
      const current = prev[batchId];
      if (!current?.selected) return prev;
      const currentQty = typeof current.quantity === 'number' ? current.quantity : (parseInt(current.quantity, 10) || 0);
      const newQty = Math.min(currentQty + 1, maxQty);
      return { ...prev, [batchId]: { ...current, quantity: newQty } };
    });
  };

  // Decrement batch quantity
  const decrementBatchQty = (batchId) => {
    setSelectedBatches(prev => {
      const current = prev[batchId];
      if (!current?.selected) return prev;
      const currentQty = typeof current.quantity === 'number' ? current.quantity : (parseInt(current.quantity, 10) || 0);
      const newQty = Math.max(currentQty - 1, 0);
      return { ...prev, [batchId]: { ...current, quantity: newQty } };
    });
  };

  // Calculate total selected quantity
  const totalSelectedQty = useMemo(() => {
    return Object.values(selectedBatches).reduce((sum, b) => {
      const qty = typeof b.quantity === 'number' ? b.quantity : (parseInt(b.quantity, 10) || 0);
      return sum + (b.selected ? qty : 0);
    }, 0);
  }, [selectedBatches]);

  // Select all batches - with quantity 1 each
  const selectAllBatches = () => {
    const newSelection = {};
    sourceBatches.forEach(b => {
      const defaultQty = b.quantity >= 1 ? 1 : b.quantity;
      newSelection[b.id] = { selected: true, quantity: defaultQty };
    });
    setSelectedBatches(newSelection);
  };

  // Deselect all batches
  const deselectAllBatches = () => {
    setSelectedBatches({});
  };

  // Add current selection to transfer list
  const addToTransferList = () => {
    const batchesToAdd = Object.entries(selectedBatches)
      .filter(([_, v]) => {
        const qty = typeof v.quantity === 'number' ? v.quantity : (parseInt(v.quantity, 10) || 0);
        return v.selected && qty > 0;
      })
      .map(([batchId, v]) => {
        const batch = sourceBatches.find(b => b.id === batchId);
        return {
          batch_id: batchId,
          quantity: typeof v.quantity === 'number' ? v.quantity : (parseInt(v.quantity, 10) || 0),
          mrp: batch?.mrp,
          expiry_date: batch?.expiry_date,
        };
      });

    if (batchesToAdd.length === 0) {
      toast.error("Please select at least one batch with quantity");
      return;
    }

    if (!sourceShop || !destShop) {
      toast.error("Please select source and destination shops");
      return;
    }

    // Create transfer list item
    const item = {
      id: `${selectedProduct.id}-${Date.now()}`, // Unique ID for this list item
      product: selectedProduct,
      sourceShop,
      destShop,
      batches: batchesToAdd,
      totalQty: batchesToAdd.reduce((sum, b) => sum + b.quantity, 0),
    };

    // Add to top of list
    setTransferList(prev => [item, ...prev]);
    
    // Clear current selection but keep shops
    setSelectedProduct(null);
    setProductStock(null);
    setSelectedBatches({});
    
    toast.success(`Added ${item.product.product} (${item.totalQty} units) to transfer list`);
  };

  // Remove item from transfer list
  const removeFromTransferList = (itemId) => {
    setTransferList(prev => prev.filter(item => item.id !== itemId));
  };

  // Clear entire transfer list
  const clearTransferList = () => {
    setTransferList([]);
  };

  // Calculate total items in transfer list
  const transferListTotalQty = useMemo(() => {
    return transferList.reduce((sum, item) => sum + item.totalQty, 0);
  }, [transferList]);

  // Check if shops are locked (transfer list has items)
  const shopsLocked = transferList.length > 0;

  // Transfer stock with batch selection - handles both transfer list and current selection
  const handleTransfer = async () => {
    // Build list of all items to transfer
    let itemsToTransfer = [...transferList];
    
    // Also include current selection if any
    const currentBatches = Object.entries(selectedBatches)
      .filter(([_, v]) => {
        const qty = typeof v.quantity === 'number' ? v.quantity : (parseInt(v.quantity, 10) || 0);
        return v.selected && qty > 0;
      })
      .map(([batchId, v]) => {
        const batch = sourceBatches.find(b => b.id === batchId);
        return { 
          batch_id: batchId, 
          quantity: typeof v.quantity === 'number' ? v.quantity : (parseInt(v.quantity, 10) || 0),
          mrp: batch?.mrp,
          expiry_date: batch?.expiry_date,
        };
      });

    if (currentBatches.length > 0 && selectedProduct && sourceShop && destShop) {
      itemsToTransfer = [{
        id: `current-${Date.now()}`,
        product: selectedProduct,
        sourceShop,
        destShop,
        batches: currentBatches,
        totalQty: currentBatches.reduce((sum, b) => sum + b.quantity, 0),
      }, ...itemsToTransfer];
    }

    if (itemsToTransfer.length === 0) {
      toast.error("Please add items to transfer");
      return;
    }

    setTransferring(true);
    let successCount = 0;
    let failCount = 0;
    let totalTransferred = 0;

    for (const item of itemsToTransfer) {
      try {
        const { data } = await api.post("/stock/transfer-batches", {
          product_id: item.product.id,
          source_shop_id: item.sourceShop,
          destination_shop_id: item.destShop,
          batches: item.batches.map(b => ({ batch_id: b.batch_id, quantity: b.quantity })),
        });
        
        successCount++;
        totalTransferred += data.total_transferred;
      } catch (e) {
        failCount++;
        toast.error(`Failed to transfer ${item.product.product}: ${formatApiError(e)}`);
      }
    }

    if (successCount > 0) {
      const shopFrom = shops.find(s => s.id === itemsToTransfer[0].sourceShop)?.name;
      const shopTo = shops.find(s => s.id === itemsToTransfer[0].destShop)?.name;
      toast.success(`Transferred ${totalTransferred} units (${successCount} products) from ${shopFrom} to ${shopTo}`);
      
      // Remember the shops for next transfer
      setLastUsedSource(itemsToTransfer[0].sourceShop);
      setLastUsedDest(itemsToTransfer[0].destShop);
      try {
        localStorage.setItem("transfer_last_source", itemsToTransfer[0].sourceShop);
        localStorage.setItem("transfer_last_dest", itemsToTransfer[0].destShop);
      } catch (_e) {
        // localStorage may fail in private browsing mode or when storage is full.
        // Non-critical: user experience continues normally without saved preferences.
        console.debug("localStorage unavailable for transfer preferences");
      }
    }

    // Clear everything on success
    if (failCount === 0) {
      setTransferList([]);
      setSelectedProduct(null);
      setProductStock(null);
      setSelectedBatches({});
      setSourceShop("");
      setDestShop("");
      setPrefillWarning(null); // Clear stale recommendation warning
    } else {
      // Partial success - just refresh current product if selected
      if (selectedProduct) {
        fetchProductStock(selectedProduct.id);
      }
      setSelectedBatches({});
    }

    setTransferring(false);
  };

  // Fetch transfer history
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/stock/transfer-history", { params: { limit: 50 } });
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  return (
    <div className="space-y-4" data-testid="stock-transfer">
      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onDetected={handleBarcodeScan}
      />
      
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-semibold text-base sm:text-lg">Stock Transfer</h2>
          <p className="text-[10px] sm:text-xs text-muted-foreground">Transfer batches between shops (FEFO)</p>
        </div>
        <Button variant="outline" size="sm" onClick={openHistory} className="shrink-0 h-8 sm:h-9 text-xs sm:text-sm" data-testid="transfer-history-btn">
          <History className="w-4 h-4 sm:mr-1" /> 
          <span className="hidden sm:inline">History</span>
        </Button>
      </div>

      {/* Product Search with Barcode/Search Toggle */}
      {!selectedProduct && (
        <Card className="p-3 sm:p-4">
          {/* Mode Toggle: Barcode | Search */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={searchMode === "scan" ? "default" : "outline"}
              onClick={() => { setSearchMode("scan"); setScanOpen(true); }}
              style={searchMode === "scan" ? { backgroundColor: "#9E6D5B" } : {}}
              className="flex-1 sm:flex-none text-xs sm:text-sm px-3 sm:px-4"
              data-testid="transfer-scan-btn"
            >
              <ScanBarcode className="w-4 h-4 mr-1.5 sm:mr-2" /> 
              <span className="hidden xs:inline">Scan</span> Barcode
            </Button>
            <Button
              variant={searchMode === "search" ? "default" : "outline"}
              onClick={() => setSearchMode("search")}
              style={searchMode === "search" ? { backgroundColor: "#9E6D5B" } : {}}
              className="flex-1 sm:flex-none text-xs sm:text-sm px-3 sm:px-4"
              data-testid="transfer-search-btn"
            >
              <Search className="w-4 h-4 mr-1.5 sm:mr-2" /> Search
            </Button>
          </div>

          {/* Search Input (shown when search mode is active) */}
          {searchMode === "search" && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by manual barcode, auto barcode, product, group or brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="transfer-product-search"
                autoFocus
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  Searching...
                </div>
              )}

              {/* Results */}
              {searchResults.length > 0 && (
                <Card className="absolute z-50 w-full mt-1 max-h-64 overflow-y-auto shadow-lg">
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      className="w-full p-3 text-left hover:bg-muted border-b last:border-b-0 transition-colors"
                      onClick={() => selectProduct(p)}
                      data-testid={`transfer-result-${p.id}`}
                    >
                      <div className="font-medium">{p.product}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.brand_name} · {p.auto_barcode}
                        {p.manual_barcode && ` · ${p.manual_barcode}`}
                        {p.mrp && ` · MRP ₹${Number(p.mrp).toFixed(2)}`}
                      </div>
                    </button>
                  ))}
                </Card>
              )}
            </div>
          )}

          {/* Empty State */}
          {searchMode === "search" && !searchQuery && (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Search for a product to transfer stock between shops.</p>
              <p className="text-xs mt-1">Select specific batches by expiry date for precise inventory control.</p>
            </div>
          )}
        </Card>
      )}

      {/* Selected Product & Transfer Form */}
      {selectedProduct && (
        <Card className="p-3 sm:p-4">
          {/* Product Info */}
          <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b">
            <div className="flex items-start gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#9E6D5B]/10 flex items-center justify-center shrink-0">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-[#9E6D5B]" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm sm:text-base truncate">{selectedProduct.product}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {selectedProduct.brand_name} · {selectedProduct.auto_barcode}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clearSelection} className="shrink-0 h-8 w-8 p-0" data-testid="transfer-clear-product-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Current Stock Summary */}
          {productStock && (
            <div className="mb-3 sm:mb-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Current Stock by Shop</div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {productStock.shops.map((s) => (
                  <div
                    key={s.shop_id}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm ${
                      s.total_quantity > 0
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <span className="font-medium">{s.shop_name}</span>
                    <span className="mx-1">:</span>
                    <span className="font-mono">{s.total_quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shop Selection - Stacks on mobile */}
          {shopsLocked && (
            <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Shops are locked to match existing items in transfer list. Clear the list to change shops.</span>
            </div>
          )}
          <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-5 sm:gap-3 sm:items-end mb-3 sm:mb-4">
            {/* Source Shop */}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">From Shop</Label>
              <Select 
                value={sourceShop} 
                onValueChange={(v) => { setSourceShop(v); if (v === destShop) setDestShop(""); }}
                disabled={shopsLocked}
              >
                <SelectTrigger className="mt-1 h-10 sm:h-9" data-testid="transfer-source-shop">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {shops.map((shop) => {
                    const stock = getShopStock(shop.id);
                    return (
                      <SelectItem key={shop.id} value={shop.id} disabled={stock <= 0}>
                        {shop.name} ({stock} in stock)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Arrow - horizontal on mobile */}
            <div className="flex items-center justify-center py-1 sm:py-0">
              <ArrowRight className="w-5 h-5 text-muted-foreground rotate-90 sm:rotate-0" />
            </div>

            {/* Destination Shop */}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">To Shop</Label>
              <Select value={destShop} onValueChange={setDestShop} disabled={shopsLocked}>
                <SelectTrigger className="mt-1 h-10 sm:h-9" data-testid="transfer-dest-shop">
                  <SelectValue placeholder="Select destination" />
                </SelectTrigger>
                <SelectContent>
                  {shops.map((shop) => (
                    <SelectItem key={shop.id} value={shop.id} disabled={shop.id === sourceShop}>
                      {shop.name} ({getShopStock(shop.id)} in stock)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stale Recommendation Warning (Phase 2D) */}
          {prefillWarning && (
            <div data-testid={prefillWarning.type === 'zero' ? 'stale-rec-error' : 'stale-rec-warning'} className={`rounded-lg p-3 mb-3 sm:mb-4 flex items-start gap-3 ${
              prefillWarning.type === 'zero' 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                prefillWarning.type === 'zero' ? 'text-red-600' : 'text-amber-600'
              }`} />
              <div className="flex-1">
                {prefillWarning.type === 'zero' ? (
                  <>
                    <div className="font-medium text-red-800">Recommendation No Longer Available</div>
                    <div className="text-sm text-red-700 mt-1">
                      The source shop currently has no transferable stock for this product.
                      This recommendation was for {prefillWarning.recommendedQty} units.
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                      data-testid="stale-rec-return-btn"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductStock(null);
                        setSelectedBatches({});
                        setPrefillWarning(null);
                      }}
                    >
                      Return to Recommendations
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-amber-800">Stock Availability Changed</div>
                    <div className="text-sm text-amber-700 mt-1">
                      Only <strong>{prefillWarning.actualQty}</strong> of the recommended <strong>{prefillWarning.recommendedQty}</strong> units 
                      are currently available for transfer. The selection has been adjusted accordingly.
                    </div>
                  </>
                )}
              </div>
              {prefillWarning.type !== 'zero' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setPrefillWarning(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}

          {/* Batch Selection Section */}
          {sourceShop && sourceBatches.length > 0 && (
            <div className="border rounded-lg overflow-hidden mb-3 sm:mb-4">
              <div className="bg-muted/50 px-2 sm:px-3 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#9E6D5B] shrink-0" />
                  <div className="text-xs">
                    <span className="font-medium uppercase tracking-wider">Batches in {shops.find(s => s.id === sourceShop)?.name}</span>
                    <span className="text-muted-foreground hidden sm:inline"> (FEFO)</span>
                  </div>
                </div>
                <div className="flex gap-2 self-end sm:self-auto">
                  <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={selectAllBatches}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={deselectAllBatches}>
                    Clear
                  </Button>
                </div>
              </div>
              
              {/* Desktop Table */}
              <table className="w-full text-sm table-fixed hidden sm:table">
                <thead className="bg-muted/30 text-xs">
                  <tr>
                    <th className="p-2 text-left w-[40px]"></th>
                    <th className="p-2 text-right w-[15%]">MRP</th>
                    <th className="p-2 text-left w-[20%]">Expiry</th>
                    <th className="p-2 text-right w-[15%]">Available</th>
                    <th className="p-2 text-center w-[20%]">Days Left</th>
                    <th className="p-2 text-right w-[25%]">Transfer Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceBatches.map((batch) => {
                    const expiry = getExpiryStatus(batch.days_until_expiry);
                    const batchState = selectedBatches[batch.id] || { selected: false, quantity: 0 };
                    
                    return (
                      <tr 
                        key={batch.id} 
                        className={`border-t transition-colors ${batchState.selected ? "bg-[#9E6D5B]/5" : "hover:bg-muted/30"}`}
                      >
                        <td className="p-2 text-center">
                          <Checkbox
                            checked={batchState.selected}
                            onCheckedChange={() => toggleBatch(batch.id, batch.quantity)}
                            data-testid={`batch-checkbox-${batch.id}`}
                          />
                        </td>
                        <td className="p-2 text-right font-mono text-xs">
                          {batch.mrp !== null ? `₹${Number(batch.mrp).toFixed(0)}` : "N/A"}
                        </td>
                        <td className="p-2 text-xs font-mono">
                          {batch.expiry_date || <span className="text-muted-foreground">N/A</span>}
                        </td>
                        <td className="p-2 text-right font-mono font-semibold">
                          {batch.quantity}
                        </td>
                        <td className="p-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${expiry.color}`}>
                            {expiry.label}
                          </span>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => decrementBatchQty(batch.id)}
                              disabled={!batchState.selected}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              max={batch.quantity}
                              className="w-14 h-7 text-xs font-mono text-center"
                              value={batchState.quantity || ""}
                              onChange={(e) => updateBatchQty(batch.id, e.target.value, batch.quantity)}
                              disabled={!batchState.selected}
                              data-testid={`batch-qty-${batch.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => incrementBatchQty(batch.id, batch.quantity)}
                              disabled={!batchState.selected}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Mobile Card Layout */}
              <div className="sm:hidden divide-y">
                {sourceBatches.map((batch) => {
                  const expiry = getExpiryStatus(batch.days_until_expiry);
                  const batchState = selectedBatches[batch.id] || { selected: false, quantity: 0 };
                  
                  return (
                    <div 
                      key={batch.id} 
                      className={`p-3 transition-colors ${batchState.selected ? "bg-[#9E6D5B]/5" : ""}`}
                    >
                      {/* Top Row: Checkbox, MRP, Expiry Badge, Available */}
                      <div className="flex items-center gap-3 mb-3">
                        <Checkbox
                          checked={batchState.selected}
                          onCheckedChange={() => toggleBatch(batch.id, batch.quantity)}
                          className="h-5 w-5"
                          data-testid={`batch-checkbox-mobile-${batch.id}`}
                        />
                        <div className="flex-1 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold">
                              {batch.mrp !== null ? `₹${Number(batch.mrp).toFixed(0)}` : "N/A"}
                            </span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${expiry.color}`}>
                              {expiry.label}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <span className="font-mono">Exp: {batch.expiry_date || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bottom Row: Available and Qty Controls */}
                      <div className="flex items-center justify-between pl-8">
                        <div className="text-xs">
                          <span className="text-muted-foreground">Available: </span>
                          <span className="font-mono font-semibold">{batch.quantity}</span>
                        </div>
                        
                        {/* Quantity Controls with +/- buttons */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0 touch-manipulation"
                            onClick={() => decrementBatchQty(batch.id)}
                            disabled={!batchState.selected}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            max={batch.quantity}
                            className="w-14 h-9 text-sm font-mono text-center"
                            value={batchState.quantity || ""}
                            onChange={(e) => updateBatchQty(batch.id, e.target.value, batch.quantity)}
                            disabled={!batchState.selected}
                            data-testid={`batch-qty-mobile-${batch.id}`}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0 touch-manipulation"
                            onClick={() => incrementBatchQty(batch.id, batch.quantity)}
                            disabled={!batchState.selected}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No batches message */}
          {sourceShop && sourceBatches.length === 0 && (
            <div className="text-center py-6 text-muted-foreground border rounded-lg mb-3 sm:mb-4">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No batches available in this shop</p>
            </div>
          )}

          {/* Transfer Summary & Buttons */}
          {sourceShop && destShop && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t pt-3 sm:pt-4">
              <div className="text-xs sm:text-sm">
                {totalSelectedQty > 0 ? (
                  <span>
                    Selected <span className="font-mono font-semibold text-[#9E6D5B]">{totalSelectedQty}</span> units
                    {" from "}
                    <span className="font-medium">{shops.find(s => s.id === sourceShop)?.name}</span>
                    {" → "}
                    <span className="font-medium">{shops.find(s => s.id === destShop)?.name}</span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select batches to add or transfer</span>
                )}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                {/* ADD Button */}
                <Button
                  variant="outline"
                  onClick={addToTransferList}
                  disabled={transferring || totalSelectedQty === 0}
                  className="flex-1 sm:flex-none h-10 sm:h-9 border-[#9E6D5B] text-[#9E6D5B] hover:bg-[#9E6D5B]/10"
                  data-testid="transfer-add-btn"
                >
                  <ListPlus className="w-4 h-4 mr-2" />
                  Add to List
                </Button>
                {/* TRANSFER Button */}
                <Button
                  onClick={handleTransfer}
                  disabled={transferring || (totalSelectedQty === 0 && transferList.length === 0)}
                  className="flex-1 sm:flex-none text-white h-10 sm:h-9"
                  style={{ backgroundColor: "#9E6D5B" }}
                  data-testid="transfer-submit-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {transferring ? "Transferring..." : `Transfer${transferList.length > 0 ? ` All (${transferList.length + (totalSelectedQty > 0 ? 1 : 0)})` : ""}`}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Transfer List */}
      {transferList.length > 0 && (
        <Card className="p-3 sm:p-4" data-testid="transfer-list-card">
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <div className="flex items-center gap-2">
              <ListPlus className="w-4 h-4 text-[#9E6D5B]" />
              <span className="font-semibold text-sm">
                Transfer List 
                <span className="text-muted-foreground font-normal ml-2">
                  ({transferList.length} product{transferList.length > 1 ? "s" : ""}, {transferListTotalQty} units)
                </span>
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={clearTransferList}
              data-testid="transfer-list-clear-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear All
            </Button>
          </div>

          {/* Transfer direction header */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
            <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded font-medium">
              {shops.find(s => s.id === transferList[0].sourceShop)?.name}
            </span>
            <ArrowRight className="w-3 h-3" />
            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-medium">
              {shops.find(s => s.id === transferList[0].destShop)?.name}
            </span>
          </div>

          {/* List Table - Desktop */}
          <div className="hidden sm:block border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-left">Batches</th>
                  <th className="p-2 text-center">Qty</th>
                  <th className="p-2 text-center w-10"></th>
                </tr>
              </thead>
              <tbody>
                {transferList.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-muted/20">
                    <td className="p-2">
                      <div className="font-medium text-sm">{item.product.product}</div>
                      <div className="text-xs text-muted-foreground">{item.product.brand_name}</div>
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {item.batches.map((b, idx) => (
                          <span 
                            key={`${item.id}-${b.batch_id}-${idx}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono"
                          >
                            {b.mrp !== null && <span>₹{Number(b.mrp).toFixed(0)}</span>}
                            {b.expiry_date && <span className="text-muted-foreground">· {b.expiry_date}</span>}
                            <span className="font-semibold text-[#9E6D5B]">×{b.quantity}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <span className="font-mono font-semibold">{item.totalQty}</span>
                    </td>
                    <td className="p-2 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeFromTransferList(item.id)}
                        data-testid={`transfer-list-remove-${item.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* List Cards - Mobile */}
          <div className="sm:hidden space-y-2">
            {transferList.map((item) => (
              <div key={item.id} className="border rounded-lg p-3 bg-muted/10">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{item.product.product}</div>
                    <div className="text-xs text-muted-foreground">{item.product.brand_name}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeFromTransferList(item.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {item.batches.map((b, idx) => (
                      <span 
                        key={`${item.id}-${b.batch_id}-${idx}`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono"
                      >
                        {b.mrp !== null && <span>₹{Number(b.mrp).toFixed(0)}</span>}
                        <span className="font-semibold text-[#9E6D5B]">×{b.quantity}</span>
                      </span>
                    ))}
                  </div>
                  <span className="font-mono font-semibold text-sm ml-2">Total: {item.totalQty}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Transfer All Button when no product selected */}
          {!selectedProduct && (
            <div className="mt-3 pt-3 border-t flex justify-end">
              <Button
                onClick={handleTransfer}
                disabled={transferring}
                className="text-white h-10"
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid="transfer-list-submit-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {transferring ? "Transferring..." : `Transfer All (${transferList.length} products)`}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Transfer History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Transfer History</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            {historyLoading ? (
              <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : history.length > 0 ? (
              <div className="space-y-2">
                {history.map((t) => (
                  <div key={t.id} className="p-3 border rounded-md text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{t.product_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(t.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded">{t.source_shop_name}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">{t.destination_shop_name}</span>
                      <span className="font-mono font-semibold text-foreground ml-auto">×{t.total_quantity}</span>
                    </div>
                    {/* Batch details */}
                    {t.batches_transferred && t.batches_transferred.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-dashed">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Batches</div>
                        <div className="flex flex-wrap gap-1">
                          {t.batches_transferred.map((b, idx) => (
                            <span
                              key={b.batch_id || b.id || `${t.id}-${b.mrp ?? ""}-${b.expiry_date ?? ""}-${idx}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-[10px] font-mono"
                            >
                              {b.mrp !== null && <span>₹{Number(b.mrp).toFixed(0)}</span>}
                              {b.expiry_date && <span>· {b.expiry_date}</span>}
                              <span className="font-semibold">×{b.quantity}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {t.created_by_name && (
                      <div className="text-xs text-muted-foreground mt-2">By: {t.created_by_name}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">No transfer history found.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
